import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, TrendingUp } from 'lucide-react';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ReferenceArea,
} from 'recharts';
import LabServices from '../../shared/ApiServices/LabServices';
import {
    extractLabOrdersList, extractLabOrderId, extractLabResultsList,
    formatLabDate, parseNumericRange,
} from './labUtils';

interface TrendPoint { date: string; value: number; refRange?: string }

interface ParameterTrendChartProps {
    mobile: string;
    testId: string;
    paramName: string;
    currentOrderId: string;
    onClose: () => void;
}

// Modal showing a patient's historical values for one test parameter — read-only, purely derived from
// existing endpoints (no new persistence). Caps lookups to keep this a bounded number of requests.
const ParameterTrendChart: React.FC<ParameterTrendChartProps> = ({ mobile, testId, paramName, currentOrderId, onClose }) => {
    const [points, setPoints] = useState<TrendPoint[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                if (!mobile) { setPoints([]); return; }
                const res = await LabServices.listOrders({ mobile, limit: 20 });
                const orders = extractLabOrdersList(res)
                    .filter(o => extractLabOrderId(o) !== currentOrderId)
                    .sort((a, b) => new Date(a.orderDate ?? a.createdAt ?? (a as any).created_at ?? 0).getTime()
                        - new Date(b.orderDate ?? b.createdAt ?? (b as any).created_at ?? 0).getTime())
                    .slice(-10); // most recent 10 prior orders, chronological order for the chart

                const collected: TrendPoint[] = [];
                for (const o of orders) {
                    if (cancelled) return;
                    try {
                        const results = extractLabResultsList(await LabServices.getOrderResults(extractLabOrderId(o)));
                        const match = results.find(r => (r.testId ?? (r as any).test_id) === testId);
                        const param = match?.parameters?.find(p => (p.paramName ?? (p as any).param_name) === paramName);
                        const val = Number(param?.value);
                        if (param && !isNaN(val)) {
                            collected.push({
                                date: formatLabDate(o.orderDate ?? o.createdAt ?? (o as any).created_at),
                                value: val,
                                refRange: param.referenceRange ?? (param as any).reference_range,
                            });
                        }
                    } catch { /* skip this order, keep going */ }
                }
                if (!cancelled) setPoints(collected);
            } catch {
                if (!cancelled) setPoints([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [mobile, testId, paramName, currentOrderId]);

    const range = points.find(p => p.refRange)?.refRange;
    const parsedRange = parseNumericRange(range);

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
            <div className="w-full max-w-xl rounded-2xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] shadow-2xl flex flex-col max-h-[80vh]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--v2-border-subtle)]">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" style={{ color: '#045EB8' }} strokeWidth={2} />
                        <h2 className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">{paramName} — Trend</h2>
                    </div>
                    <button type="button" onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-[var(--v2-text-muted)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-surface)] transition-colors border-0 bg-transparent">
                        <X className="w-4 h-4" strokeWidth={2} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="w-6 h-6 rounded-full border-2 border-[#045EB8] border-t-transparent animate-spin" />
                        </div>
                    ) : points.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 py-16 text-center">
                            <TrendingUp className="w-8 h-8 text-[var(--v2-text-muted)]" strokeWidth={1.5} />
                            <p className="text-[12px] font-semibold text-[var(--v2-text-secondary)]">No prior results for this parameter yet</p>
                        </div>
                    ) : (
                        <div style={{ width: '100%', height: 280 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--v2-border-subtle)" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="var(--v2-text-muted)" />
                                    <YAxis tick={{ fontSize: 10 }} stroke="var(--v2-text-muted)" domain={['auto', 'auto']} />
                                    <RechartsTooltip contentStyle={{ background: 'var(--v2-bg-card)', border: '1px solid var(--v2-border-card)', borderRadius: 8, fontSize: 11 }} />
                                    {parsedRange && (
                                        <ReferenceArea y1={parsedRange[0]} y2={parsedRange[1]} fill="#059669" fillOpacity={0.08} />
                                    )}
                                    <Line type="monotone" dataKey="value" stroke="#045EB8" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ParameterTrendChart;
